/**
 * Convert dot notation to bracket notation.
 * e.g. 'user.name' to 'user[name]'
 * @param {string} dotNotation
 * @returns {string}
 */
function convertDotToBracketNotation(dotNotation) {
    if (dotNotation.indexOf('.') === -1) {
        return dotNotation;
    }

    return dotNotation
        .split(/\./u)
        .map((segment, index) => {
            if (index === 0) {
                return segment;
            }

            return `[${segment}]`;
        })
        .join('');
}

// convert bracket notation to dot notation
// e.g. 'user[name]' to 'user.name'
function convertBracketToDotNotation(bracketNotation) {
    if (bracketNotation.indexOf('[') === -1) {
        return bracketNotation;
    }

    return bracketNotation
        .split(/[\[\]]/u)
        .filter(segment => segment !== '')
        .join('.');
}

function getFieldValue(element) {
    if (!element) {
        return { type: null, value: null };
    }

    if (element.nodeName === 'SELECT') {
        const options = Array.prototype.map.call(element.selectedOptions, (opt) => opt.value);

        if (element.hasAttribute('multiple')) {
            return { type: 'string', value: options };
        }

        return { type: 'string', value: options[0] };
    }

    if (element.nodeName === 'INPUT' && element.getAttribute('flatpickr-initialized') === 'true') {
        const controller = Stimulus.getControllerForElementAndIdentifier(element, 'date-picker');

        if (element.getAttribute('data-model') === 'range') {
            throw new Error('Range date pickers are not supported, yet?');
        }

        return { type: 'date', value: controller.picker.selectedDates[0] };
    }
    //
    // if (element.nodeName === 'INPUT' && element.type === 'checkbox') {
    //     return { type: 'string', value: element.checked ? element.value : null };
    // }
    //
    // if (element.nodeName === 'INPUT' && element.type === 'radio') {
    //     return { type: 'string', value: element.checked ? element.value : null };
    // }

    if (element.nodeName === 'INPUT' && element.type === 'number') {
        return { type: 'number', value: element.valueAsNumber };
    }

    // if (element.nodeName === 'INPUT' && element.type === 'date') {
    //     return { type: 'date', value: element.valueAsDate };
    // }

    return { type: 'string', value: element.value };
}

function compareString(left, operator, right) {
    if (operator === 'in' || operator === 'nin') {
        if (typeof left.value !== 'string' && !Array.isArray(right.value)) {
            return false;
        }

        right.value = right.value.map((value) => String(value));
    } else {
        if (typeof left.value !== 'string' || typeof right.value !== 'string') {
            return false;
        }
    }

    switch (operator) {
        case 'eq':
            return left.value === right.value;
        case 'neq':
            return left.value !== right.value;
        case 'in':
            return right.value.indexOf(left.value) !== -1;
        case 'nin':
            return right.value.indexOf(left.value) === -1;
        default:
            throw new Error(`Invalid operator "${operator}" for string.`);
    }
}

function compareNumber(left, operator, right) {
    if (left.type !== 'string' && left.type !== 'number') {
        return false;
    }

    if (right.type !== 'string' && right.type !== 'number') {
        return false;
    }

    let leftValue = typeof left.value === 'string' ? Number(left.value) : left.value;
    if (isNaN(leftValue)) {
        return false;
    }

    let rightValue = typeof right.value === 'string' ? Number(right.value) : right.value;
    if (isNaN(rightValue)) {
        return false;
    }

    switch (operator) {
        case 'eq':
            return leftValue === rightValue;
        case 'neq':
            return leftValue !== rightValue;
        case 'gt':
            return leftValue > rightValue;
        case 'gte':
            return leftValue >= rightValue;
        case 'lt':
            return leftValue < rightValue;
        case 'lte':
            return leftValue <= rightValue;
        default:
            throw new Error(`Invalid operator "${operator}" for number.`);
    }
}

function compareDate(left, operator, right) {
    if ((left.type !== 'date' && left.type !== 'string') || !left.value) {
        return false;
    }

    let leftValue = left.value;
    if (typeof leftValue === 'string') {
        if (leftValue.match(/^\d{4}-\d{2}-\d{2}$/u)) {
            leftValue = new Date(`${leftValue} 00:00:00`);
        }
    } else if (leftValue instanceof Date) {
        leftValue.setHours(0, 0, 0, 0);
    }

    if ((right.type !== 'date' && right.type !== 'string') || !right.value) {
        return false;
    }

    let rightValue = right.value;
    if (typeof rightValue === 'string') {
        if (rightValue.match(/^\d{4}-\d{2}-\d{2}$/u)) {
            rightValue = new Date(`${rightValue} 00:00:00`);
        } else {
        }
    } else if (rightValue instanceof Date) {
        rightValue.setHours(0, 0, 0, 0);
    }

    switch (operator) {
        case 'eq':
            return leftValue.getTime() === rightValue.getTime();
        case 'neq':
            return leftValue.getTime() !== rightValue.getTime();
        case 'gt':
            return leftValue.getTime() > rightValue.getTime();
        case 'gte':
            return leftValue.getTime() >= rightValue.getTime();
        case 'lt':
            return leftValue.getTime() < rightValue.getTime();
        case 'lte':
            return leftValue.getTime() <= rightValue.getTime();
        default:
            throw new Error(`Invalid operator "${operator}" for date.`);
    }
}

function compare(left, operator, right, compareType) {
    switch (compareType) {
        case 'string':
            return compareString(left, operator, right);
        case 'number':
            return compareNumber(left, operator, right);
        case 'date':
            return compareDate(left, operator, right);
        default:
            throw new Error(`Invalid compare type "${compareType}".`);
    }
}

function isFieldReference(value) {
    return typeof value === 'string' && (value.startsWith('@') || value.startsWith('~.'));
}

export {
    convertDotToBracketNotation,
    convertBracketToDotNotation,
    getFieldValue,
    compare,
    isFieldReference,
};
